"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import Image from "next/image"
import {
  ChevronLeft,
  ChevronRight,
  Mails,
  Search,
  X,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { CtaBanner } from "@/components/sections/cta-banner"
import { AnimateIn } from "@/components/ui/animate-in"
import { cn } from "@/lib/utils"

const FALLBACK_IMAGE = "/images/og-default.webp"
const ARTICLES_PER_PAGE = 9

export type UIArticle = {
  slug: string
  title: string
  date: string
  category: "news" | "promotion"
  excerpt: string
  imageUrl: string | null
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

function categoryLabel(cat: string) {
  return cat === "news" ? "Actualité" : "Promotion"
}

type Filter = "all" | "news" | "promotion"

type Props = {
  articles: UIArticle[]
}

export function ActualitesClient({ articles }: Props) {
  const sorted = useMemo(
    () =>
      [...articles].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [articles],
  )

  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState<Filter>("all")
  const [searchFocused, setSearchFocused] = useState(false)

  const filtered = useMemo(() => {
    let result = sorted

    if (filter !== "all") {
      result = result.filter((a) => a.category === filter)
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim()
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q),
      )
    }

    return result
  }, [search, filter, sorted])

  const totalPages = Math.ceil(filtered.length / ARTICLES_PER_PAGE)
  const effectivePage = Math.min(currentPage, totalPages || 1)
  const startIndex = (effectivePage - 1) * ARTICLES_PER_PAGE
  const pageArticles = filtered.slice(startIndex, startIndex + ARTICLES_PER_PAGE)

  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  function handleFilterChange(f: Filter) {
    setFilter(f)
    setCurrentPage(1)
  }

  function handleSearchChange(value: string) {
    setSearch(value)
    setCurrentPage(1)
  }

  return (
    <>
      <section className="mx-auto max-w-7xl px-6 pt-20 pb-10">
        {/* Header */}
        <AnimateIn>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="text-balance font-semibold text-4xl tracking-tight">
                Nos actualit&eacute;s
              </h1>
              <p className="mt-2 text-balance text-lg text-muted-foreground tracking-normal sm:text-xl">
                Suivez les derni&egrave;res nouvelles d&apos;ADIS 95.
              </p>
            </div>
            <Button
              className="hidden gap-3 sm:inline-flex active:scale-[0.97]"
              size="lg"
              variant="secondary"
              asChild
            >
              <Link href="/contact">
                <Mails />
                <span className="hidden lg:inline">Nous contacter</span>
                <span className="hidden md:inline lg:hidden">Contact</span>
              </Link>
            </Button>
          </div>
        </AnimateIn>

        <Separator className="mt-7 mb-10" />

        {/* Intro text for SEO */}
        <AnimateIn delay={30}>
          <div className="mb-12 max-w-[65ch]">
            <p className="text-muted-foreground leading-relaxed">
              Retrouvez ici toutes les actualités d&apos;ADIS 95 : promotions exclusives sur l&apos;outillage professionnel,
              nouveautés produits des marques Festool, CMT, Makita et Leman, informations sur nos services d&apos;affûtage,
              conseils techniques pour l&apos;entretien de vos outils coupants, et événements à venir dans notre atelier
              de Méry-sur-Oise. Nous partageons régulièrement nos offres spéciales et nos conseils d&apos;experts pour vous
              accompagner dans votre métier du bois. Que vous soyez menuisier, ébéniste, agenceur ou industriel,
              restez informé des dernières tendances et innovations du secteur.
            </p>
          </div>
        </AnimateIn>

        {/* Search bar + Tag filters */}
        <AnimateIn delay={40}>
          <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Search */}
            <div
              className={cn(
                "relative flex items-center rounded-full border bg-muted/30 transition-[transform,opacity,box-shadow,ring-color,border-color,background-color,color] duration-300 ease-[var(--ease-out-expo)]",
                searchFocused
                  ? "w-full sm:w-96 border-primary/40 ring-2 ring-primary/10 bg-background"
                  : "w-full sm:w-72 border-border",
              )}
            >
              <Search
                className="ml-4 size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Rechercher un article..."
                aria-label="Rechercher un article"
                className="w-full bg-transparent px-3 py-2.5 text-sm outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:rounded-full placeholder:text-muted-foreground/60"
              />
              {search && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="mr-3 rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Effacer la recherche"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>

            {/* Tag filters */}
            <div className="flex items-center gap-2">
              {(
                [
                  { value: "all" as Filter, label: "Tout" },
                  { value: "news" as Filter, label: "Actualités" },
                  { value: "promotion" as Filter, label: "Promotions" },
                ]
              ).map((tag) => (
                <button
                  key={tag.value}
                  onClick={() => handleFilterChange(tag.value)}
                  className={cn(
                    "rounded-full px-4 py-2 text-sm font-medium transition-[transform,opacity,box-shadow,ring-color,border-color,background-color,color] duration-200",
                    filter === tag.value
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {tag.label}
                  {tag.value !== "all" && (
                    <span className="ml-1.5 text-xs opacity-70">
                      {sorted.filter((a) =>
                        tag.value === "all" ? true : a.category === tag.value,
                      ).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </AnimateIn>

        {/* Results count */}
        {(search || filter !== "all") && (
          <p className="mb-6 text-sm text-muted-foreground">
            {filtered.length} article{filtered.length !== 1 ? "s" : ""} trouvé
            {filtered.length !== 1 ? "s" : ""}
            {search && <> pour &laquo;&nbsp;{search}&nbsp;&raquo;</>}
            {filter !== "all" && (
              <> dans {filter === "news" ? "Actualités" : "Promotions"}</>
            )}
          </p>
        )}

        {/* Blog grid */}
        <AnimateIn delay={50}>
          {pageArticles.length > 0 ? (
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {pageArticles.map((post) => (
                <Link
                  href={`/actualites/${post.slug}`}
                  key={post.slug}
                  className="group"
                >
                  <Card className="gap-3 bg-muted/30 py-0 shadow-none transition-[box-shadow,ring-color] duration-200 ease-[var(--ease-out-expo)] hover:shadow-[var(--shadow-md)] hover:ring-foreground/10">
                    <CardHeader className="p-1.5 pb-0">
                      <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                        <Image
                          alt={post.title}
                          className="object-cover transition-transform duration-300 ease-[var(--ease-premium)] group-hover:scale-105"
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          src={post.imageUrl ?? FALLBACK_IMAGE}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pt-0 pb-5">
                      <Badge variant="outline">
                        {categoryLabel(post.category)}
                      </Badge>

                      <h2 className="mt-4 font-semibold text-[1.25rem] tracking-[-0.015em] leading-snug">
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="mt-5 flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          {formatDate(post.date)}
                        </span>
                        <span className="text-sm font-medium text-primary transition-[color,transform] duration-200 ease-[var(--ease-out-expo)] group-hover:translate-x-0.5">
                          Lire &rarr;
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Search className="mb-4 size-10 text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">
                Aucun article trouvé
              </p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                Essayez un autre terme de recherche ou changez de filtre.
              </p>
              <Button
                variant="secondary"
                className="mt-4"
                onClick={() => {
                  setSearch("")
                  setFilter("all")
                  setCurrentPage(1)
                }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </AnimateIn>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2">
            <Button
              variant="secondary"
              size="icon-lg"
              className="active:scale-[0.97] min-h-[44px] min-w-[44px]"
              onClick={() => goToPage(effectivePage - 1)}
              disabled={effectivePage === 1}
              aria-label="Page précédente"
            >
              <ChevronLeft className="size-4" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={page === effectivePage ? "default" : "secondary"}
                size="icon-lg"
                className="active:scale-[0.97] min-h-[44px] min-w-[44px]"
                onClick={() => goToPage(page)}
                aria-current={page === effectivePage ? "page" : undefined}
                aria-label={`Page ${page}`}
              >
                {page}
              </Button>
            ))}
            <Button
              variant="secondary"
              size="icon-lg"
              className="active:scale-[0.97] min-h-[44px] min-w-[44px]"
              onClick={() => goToPage(effectivePage + 1)}
              disabled={effectivePage === totalPages}
              aria-label="Page suivante"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </section>

      <CtaBanner />
    </>
  )
}
